import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, FadeUp } from "@/components/site/Reveal";

const STEPS = [
  { n: "01", t: "Share the requirement", d: "A call or message with the patient's condition, location and insurance details is enough to begin." },
  { n: "02", t: "Compare across the network", d: "We bring back verified options — hospitals, labs or caregivers — with transparent costs." },
  { n: "03", t: "Book and coordinate", d: "Admissions, test slots and home visits are scheduled and tracked by a single advisor." },
  { n: "04", t: "Close the loop", d: "Claims, reports and follow-ups are completed so nothing is left pending." },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="how" ref={ref} className="relative py-24 sm:py-32 bg-white overflow-hidden" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative aspect-[4/5] rounded-[32px] overflow-hidden" data-testid="how-photo">
            <motion.img style={{ y, scale: 1.18 }} src="/images/doctor.jpg" alt="Smiling doctor" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/90 backdrop-blur-xl p-5">
              <p className="font-display font-bold text-[#14083E] text-lg leading-snug">Patients get clarity. Providers get the right patients. That is what a good aggregator does.</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <FadeUp>
            <Eyebrow>The aggregator advantage</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold tracking-[-0.03em] leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-[#14083E]">
              Four steps, <span className="text-brand-gradient">zero</span> running around.
            </h2>
          </FadeUp>
          <div className="mt-12 divide-y divide-[#14083E]/10 border-y border-[#14083E]/10">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.06}>
                <div className="group grid grid-cols-[56px_1fr] sm:grid-cols-[80px_1fr] gap-4 py-6" data-testid={`how-step-${s.n}`}>
                  <span className="font-mono-x text-sm text-[#c8106a] pt-1">{s.n}</span>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-[#14083E] group-hover:translate-x-1 transition-transform">{s.t}</h3>
                    <p className="mt-1 text-slate-600">{s.d}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
